
import { Component, Input } from '@angular/core';
import { LucideAngularModule, X } from 'lucide-angular';
import { AlertService } from '@app/core/services/alert.service';
import { alertTypes } from '@shared/components/alert/alert-types';
import { AlertTypes } from '@shared/interfaces/alert/alert-types';
import { NgClass } from '@angular/common';

/**
 * A reusable alert component supporting various types (e.g., danger, success),
 * optional close buttons, and animation.
 */
@Component({
  selector: 'app-alert',
  imports: [
    LucideAngularModule,
    NgClass
  ],
  templateUrl: './alert.component.html',
  styleUrl: './alert.component.css'
})
export class AlertComponent {
  /**
   * The type of alert to display (e.g., 'danger', 'success').
   * This maps to a predefined set of visual configurations in `alertTypes`.
   * @default 'danger'
   */
  @Input() public type: string = 'danger';

  /**
   * Optional identifier used to reference the alert.
   * When provided, allows the alert to be programmatically closed via the AlertService.
   */
  @Input() public id?: string;

  /**
   * Whether the alert can be manually closed by the user.
   * @default true
   */
  @Input() public closeable?: boolean = true;

  /**
   * Enables fade-in/fade-out animation if set to true.
   */
  @Input() public animation?: boolean = false;

  /** Lucide 'X' icon for close button */
  protected readonly x = X;

  /**
   * Indicates whether the alert is currently visible.
   * This can be used in the template for conditional rendering.
   */
  public open: boolean = true;

  /**
   * A map of visual styles and icons by alert type.
   * Populated from shared `alertTypes` constant.
   */
  public types: { [key: string]: AlertTypes } = alertTypes;

  constructor(
    private readonly alertService: AlertService,
  ) { }

  /**
   * Closes the alert. If an `id` is provided, it will delegate the closure
   * to the AlertService to manage global alert state.
   */
  public close(): void {
    if (this.id) {
      this.alertService.close(this.id);
    }
  }
}
